import { useInvalidatePositionQuery } from '@ui/features/Trade/hooks/useInvalidateQuery';
import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useDataChainId } from '@ui/hooks/datachain';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { useState, useCallback } from 'react';

export type DecreaseOrderUpdateInput = {
  orderHash: `0x${string}`;
  triggerPrice: bigint;
  closeQty: bigint;
  broker: number;
};

export const useBatchUpdateDecreaseOrders = ({
  onSuccess,
}: { onSuccess?: () => void } = {}) => {
  const chainId = useDataChainId();
  const invalidatePositionQuery = useInvalidatePositionQuery();

  // OneClick V2 integration
  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingBatchUpdateDecreaseOrders: isSubmittingV2,
    sendBatchUpdateDecreaseOrders: sendBatchUpdateDecreaseOrdersV2,
  } = useOneClickTradingV2();
  const mutationToastInfo = useMutationToast();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] = useState(false);

  const { writeContract, isPending } = useTxnWriteContract({
    onSuccess: () => {
      invalidatePositionQuery();
      onSuccess?.();
    },
  });

  const handleOneClickBatchUpdate = useCallback(
    async (updates: DecreaseOrderUpdateInput[]) => {
      if (!canUseOneClickTradingV2) return false;
      if (updates.length === 0) return false;

      setIsAwaitingOneClickResult(true);
      mutationToastInfo.onMutate({
        title: 'In Progress',
        description: '⚡️ One-click V2 decrease order update in progress',
      });

      try {
        // Convert to V2 tuple format: [orderHash, triggerPrice, closeQty, broker]
        const v2Updates = updates.map((u) => [
          u.orderHash,
          u.triggerPrice,
          u.closeQty,
          u.broker,
        ] as const);

        const intentHash = await sendBatchUpdateDecreaseOrdersV2(v2Updates);

        const status = await pollV2IntentStatus(intentHash);
        if (!status.success) {
          throw new Error(
            status.reason || 'One-click V2 batch update decrease orders failed',
          );
        }

        invalidatePositionQuery();

        mutationToastInfo.onSuccess({
          title: '⚡️ Completed',
          description: 'Decrease orders updated successfully via one-click V2',
          txHash: status.txnHash as `0x${string}`,
        });

        onSuccess?.();
        return true;
      } catch (err) {
        const message =
          err instanceof Error
            ? extractErrorMessage(err.message) ?? err.message
            : undefined;

        mutationToastInfo.onError({
          errorMsg: message,
          onErrorToastOpts: {
            description:
              'One-click V2 decrease order update failed, falling back to on-chain',
          },
        });

        console.warn('[OneClickV2] Batch update decrease orders failed:', err);
        return false;
      } finally {
        mutationToastInfo.onSettled();
        setIsAwaitingOneClickResult(false);
      }
    },
    [
      canUseOneClickTradingV2,
      sendBatchUpdateDecreaseOrdersV2,
      invalidatePositionQuery,
      onSuccess,
    ],
  );

  return {
    isPending: isSubmittingV2 || isAwaitingOneClickResult || isPending,
    batchUpdate: async (updates: DecreaseOrderUpdateInput[]) => {
      if (updates.length === 0) return;

      // Try OneClick V2 first
      const oneClickSuccess = await handleOneClickBatchUpdate(updates);
      if (oneClickSuccess) return;

      // Fallback to on-chain
      const address = getContractAddress('LeverUp', chainId);
      if (!address) throw new Error('LeverUp contract address missing');

      return writeContract({
        abi: TradingPortalFacet,
        address,
        functionName: 'batchUpdateDecreaseOrders',
        args: [updates],
      });
    },
  };
};
